import { useEffect, useState, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../../Context/AuthContext";
import { Authorization } from "../../../Context/Authorization";
import Preloader from "../../../shared/PreLoaders";
import { logActivity } from "../../../utils/LogActivity";
import { toast } from "react-toastify";
import Select from "react-select";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };
const cellInput = { width: "100%", padding: "5px 8px", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12, outline: "none" };

const IndividualLoanRepayment = () => {
  const [vfcNames, setVfcNames] = useState([]);
  const [loanNumbers, setLoanNumbers] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");
  const [selectedLoan, setSelectedLoan] = useState("");
  const [tableData, setTableData] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const { userId, selectedJfmcType } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const Api = process.env.REACT_APP_API;
  const [selectedDate, setSelectedDate] = useState("");

  const fetchVfcNames = async (jfmcTypeId) => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId }, config);
      const data = response.data.vfcs;
      if (Array.isArray(data)) setVfcNames(data);
      else console.error("API did not return an array", data);
    } catch (error) {
      console.log("Error fetching Data", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedJfmcType) fetchVfcNames(selectedJfmcType._id);
  }, [selectedJfmcType]);

  const handleVfcChange = async (event) => {
    const vfcId = event.target.value;
    setSelectedVfc(vfcId);
    setLoanNumbers([]);
    setSelectedLoan("");
    setTableData([]);
    setShowTable(false);
    setError(null);
    setSuccess(null);
    setLoading(true);
    if (vfcId) {
      try {
        const config = Authorization();
        const response = await fetch(
          `${Api}/api/loans/getLoanByVfcId`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...config.headers,
            },
            body: JSON.stringify({
              vfcId,
              groupType: "Individual",
            }),
          }
        );
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        const loans = data.loans || [];
        if (Array.isArray(loans)) {
          setLoanNumbers(loans.filter((loan) => loan.groupType === "Individual"));
        } else {
          throw new Error("Invalid data format: Expected an array of loans.");
        }
      } catch (error) {
        console.error("Error:", error);
        setError(`Failed to fetch loan numbers. ${error.message}`);
      }
    }
    setLoading(false);
  };

  const getId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;

    return String(value.$oid || value._id || "");
  };
  const handleLoanChange = async (event) => {
    const loanId = event.target.value;

    setSelectedLoan(loanId);
    setTableData([]);
    setShowTable(false);
    setError(null);
    setSuccess(null);

    if (!loanId) return;

    setLoading(true);

    try {
      const selectedLoanData = loanNumbers.find(
        (loan) => String(loan._id) === String(loanId)
      );

      if (!selectedLoanData) {
        throw new Error("Loan data not found.");
      }

      const config = Authorization();

      const response = await fetch(
        `${Api}/api/loans/getLoanByLoanNo`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...config.headers,
          },
          body: JSON.stringify({
            loanNo: selectedLoanData.loanNo,
            groupType: "Individual",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Failed to fetch loan details."
        );
      }

      if (data.groupType !== "Individual") {
        throw new Error("The selected loan is not an individual loan.");
      }

      const flattenedData = (data.subLoans || []).map((subLoan) => {
        const loanAmount = Number(subLoan.disbursedAmount || 0);
        const alreadyPaid = Number(subLoan.paidAmount || 0);

        return {
          ...subLoan,
          _id: getId(subLoan._id),
          loanNumber: data.loanNo,
          loanAmount,
          name: subLoan.memberId?.name || "Unknown",

          totalLoanPaid: alreadyPaid,
          balance:
            subLoan.balance !== undefined && subLoan.balance !== null
              ? Number(subLoan.balance)
              : Math.max(0, loanAmount - alreadyPaid),

          principal: 0,
          interest: 0,
          total: 0,

          // Required LoanCollection fields
          loanType: getId(data.loanType),
          vfcId: getId(data.vfcId),

          shgId: getId(data.shgId),
          memberId: getId(subLoan.memberId),
          paymentMode: "",
        };
      });
      setTableData(flattenedData);
    } catch (error) {
      console.error("Error fetching loan details:", error);
      setError(error.message || "Failed to fetch loan details.");
    } finally {
      setLoading(false);
    }
  };


  const handleInputChange = (index, field, value) => {
    setTableData((previousData) =>
      previousData.map((row, rowIndex) => {
        if (rowIndex !== index) return row;

        if (field === "paymentMode") {
          return { ...row, paymentMode: value };
        }

        const numericValue = Math.max(0, Number(value) || 0);

        if (field === "principal" || field === "interest") {
          const updatedRow = {
            ...row,
            [field]: numericValue,
          };

          const principal = Number(updatedRow.principal || 0);
          const interest = Number(updatedRow.interest || 0);

          updatedRow.total = principal + interest;

          updatedRow.balance = Math.max(
            0,
            Number(row.loanAmount || 0) -
            Number(row.totalLoanPaid || 0) -
            principal
          );

          return updatedRow;
        }

        return row;
      })
    );
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!selectedLoan) {
        setError("Please select a loan.");
        return;
      }

      if (!selectedDate) {
        setError("Please select a repayment date.");
        return;
      }

      const payableRows = tableData.filter(
        (loan) =>
          Number(loan.principal || 0) > 0 ||
          Number(loan.interest || 0) > 0
      );

      if (payableRows.length === 0) {
        setError("Enter a principal or interest amount.");
        return;
      }

      for (const loan of payableRows) {
        if (!loan.memberId || !loan._id) {
          setError("Missing member ID or sub-loan ID.");
          return;
        }

        if (!loan.paymentMode) {
          setError(`Please select a payment mode for ${loan.name}.`);
          return;
        }

        const principal = Number(loan.principal || 0);
        const interest = Number(loan.interest || 0);
        const loanAmount = Number(loan.loanAmount || 0);
        const alreadyPaid = Number(loan.totalLoanPaid || 0);

        if (principal < 0 || interest < 0) {
          setError("Payment amounts cannot be negative.");
          return;
        }

        // Recalculate balance instead of trusting the displayed balance.
        const remainingBalance = Math.max(
          0,
          loanAmount - alreadyPaid
        );

        if (principal > remainingBalance) {
          setError(
            `Principal exceeds the remaining balance of ₹${remainingBalance.toLocaleString("en-IN")} for ${loan.name}.`
          );
          return;
        }
      }

      const payload = payableRows.map((loan) => ({
        loanId: getId(selectedLoan),
        subLoanId: getId(loan._id),
        memberId: getId(loan.memberId),
        // Required by the LoanCollection schema
        vfcId: getId(loan.vfcId),
        loanType: getId(loan.loanType),
        shgId: getId(loan.shgId) || undefined,
        amountPaid: Number(loan.principal || 0),
        interestPaid: Number(loan.interest || 0),
        paymentDate: selectedDate,
        cashOrBank: loan.paymentMode,
      }));
      console.log("Loan Collection Payload:", payload);
      const config = Authorization();

      const response = await fetch(`${Api}/api/loan-collections`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...config.headers,
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.error || "Failed to save loan collections."
        );
      }

      toast.success("Loan collections saved successfully.");

      await logActivity(
        userId,
        "New Individual Loan Repayment",
        "Individual Loan Repayment Page",
        Api
      );

      // Refresh loan data so the updated balance is displayed.
      await handleLoanChange({
        target: { value: selectedLoan },
      });

      setShowTable(true);
      setSuccess("Loan collections saved successfully.");
    } catch (error) {
      console.error("Error saving loan collections:", error);

      setError(
        error.message || "Failed to save loan collections. Please try again."
      );
      setSuccess(null);
    }
  };



  const handleShowTable = () => {
    if (selectedLoan) setShowTable(true);
    else setError("Please select a loan number first.");
  };

  // react-select options
  const loanOptions = loanNumbers.flatMap((loan) =>
    loan.subLoans.map((subLoan) => ({
      value: loan._id,
      label: `${loan.loanNo} - ${subLoan.memberId?.name || "Unknown"}`
    }))
  );

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-filter-variant" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Select Loan</span>
            </div>

            {error && (
              <div style={{ marginBottom: 14, padding: "8px 14px", background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, fontSize: 13, color: "#C62828", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="mdi mdi-alert-circle-outline" /> {error}
              </div>
            )}
            {success && (
              <div style={{ marginBottom: 14, padding: "8px 14px", background: "#E8F5E9", border: "1px solid #C8E6C9", borderRadius: 8, fontSize: 13, color: "#1B5E20", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="mdi mdi-check-circle-outline" /> {success}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>JFMC</label>
                <Select
                  options={vfcNames.map((vfc) => ({ value: vfc._id, label: vfc.vfcName }))}
                  onChange={(selected) => handleVfcChange({ target: { value: selected?.value || "" } })}
                  value={vfcNames.map((vfc) => ({ value: vfc._id, label: vfc.vfcName })).find((opt) => opt.value === selectedVfc) || null}
                  placeholder="Select JFMC"
                  isSearchable
                />
              </div>
              <div>
                <label style={labelStyle}>Loan No</label>
                <Select
                  options={loanOptions}
                  onChange={(selected) => handleLoanChange({ target: { value: selected?.value || "" } })}
                  value={loanOptions.find((opt) => opt.value === selectedLoan) || null}
                  placeholder="Select Loan No"
                  isDisabled={!selectedVfc}
                  isSearchable
                />
              </div>
              <div>
                <label style={labelStyle}>Repayment Date</label>
                <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
              </div>
            </div>

            <button onClick={handleShowTable}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
              <i className="mdi mdi-file-check" /> Show Loan Details
            </button>
          </div>

          {/* ── Table Card ── */}
          {showTable && (
            <>
              <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
                  <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                    <i className="mdi mdi-table-large" />
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", flex: 1 }}>Loan Details</span>
                  <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 50 }}>{tableData.length} entries</span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>
                        {["Loan No", "Loan Amount", "Name", "Principal", "Interest", "Total", "Balance", "Payment Mode"].map((h) => (
                          <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "#4A5568", borderBottom: "1px solid #EDF2F7", background: "#F7FAFC", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((loan, index) => (
                        <tr key={loan._id} style={{ borderBottom: "1px solid #EDF2F7" }}>
                          <td style={{ padding: "10px 14px", color: "#1A202C", fontWeight: 600 }}>{loan.loanNumber}</td>
                          <td style={{ padding: "10px 14px", color: "#4A5568" }}>{loan.loanAmount}</td>
                          <td style={{ padding: "10px 14px", color: "#4A5568" }}>{loan.name}</td>
                          <td style={{ padding: "8px 10px" }}>
                            <input type="number" value={loan.principal} onChange={(e) => handleInputChange(index, "principal", e.target.value)} style={cellInput}
                              onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
                          </td>
                          <td style={{ padding: "8px 10px" }}>
                            <input type="number" value={loan.interest} onChange={(e) => handleInputChange(index, "interest", e.target.value)} style={cellInput}
                              onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
                          </td>
                          <td style={{ padding: "10px 14px", color: "#4A5568" }}>{loan.total}</td>
                          <td style={{ padding: "10px 14px", color: "#4A5568" }}>{loan.balance}</td>
                          <td style={{ padding: "8px 10px" }}>
                            <select value={loan.paymentMode || ""} onChange={(e) => handleInputChange(index, "paymentMode", e.target.value)} style={cellInput}>
                              <option value="">Select</option>
                              <option value="cash">Cash</option>
                              <option value="bank">Bank</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <button onClick={handleSubmit}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
                <i className="mdi mdi-file-check" /> Submit
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default IndividualLoanRepayment;